import { HttpTypes } from "@medusajs/types"
import React, { useState } from "react"
import Modal from "@modules/common/components/modal"
import {
  SIZE_GUIDE_CHART,
  SIZE_GUIDE_MEASUREMENTS,
  SIZE_GUIDE_FIT_ADVICE,
} from "@constants/product-page"

type OptionSelectProps = {
  option: HttpTypes.StoreProductOption
  current: string | undefined
  updateOption: (title: string, value: string) => void
  title: string
  disabled: boolean
  outOfStockValues?: Set<string>
  "data-testid"?: string
}

const OptionSelect: React.FC<OptionSelectProps> = ({
  option,
  current,
  updateOption,
  title,
  "data-testid": dataTestId,
  disabled,
  outOfStockValues,
}) => {
  const filteredOptions = (option.values ?? []).map((v) => v.value)
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false)

  return (
    <div className="flex flex-col gap-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-grey-90">
          Select {title}
        </span>
        {title.toLowerCase() === "size" && (
          <button
            type="button"
            onClick={() => setSizeGuideOpen(true)}
            className="text-sm text-brand-primary underline hover:text-brand-primary-dark transition-colors"
          >
            What is my size?
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2" data-testid={dataTestId}>
        {filteredOptions.map((v) => {
          const isSelected = v === current
          const isOutOfStock = outOfStockValues?.has(v) ?? false
          return (
            <button
              onClick={() => updateOption(option.id, v)}
              key={v}
              className={`relative rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                isSelected
                  ? "bg-brand-navy text-white"
                  : isOutOfStock
                  ? "border border-gray-200 text-gray-400 cursor-pointer"
                  : "border border-gray-300 text-gray-700 hover:border-brand-navy"
              }`}
              disabled={disabled}
              data-testid="option-button"
              title={isOutOfStock ? "Out of stock" : undefined}
            >
              {v}
              {isOutOfStock && (
                <span
                  className="pointer-events-none absolute inset-x-2 top-1/2 h-px -translate-y-1/2 bg-gray-300"
                  aria-hidden="true"
                />
              )}
            </button>
          )
        })}
      </div>

      <Modal isOpen={sizeGuideOpen} close={() => setSizeGuideOpen(false)} size="medium">
        <Modal.Title>Size Guide</Modal.Title>
        <Modal.Body>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-2 pr-4 font-semibold text-gray-900">Size</th>
                  <th className="py-2 font-semibold text-gray-900">Chest</th>
                </tr>
              </thead>
              <tbody>
                {SIZE_GUIDE_CHART.map((row) => (
                  <tr key={row.size} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-700">{row.size}</td>
                    <td className="py-2 text-gray-700">{row.chest}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 space-y-3">
            {SIZE_GUIDE_MEASUREMENTS.map((m) => (
              <p key={m.label} className="text-sm text-gray-600">
                <strong className="text-gray-900">{m.label}:</strong> {m.instructions}
              </p>
            ))}
          </div>

          <p className="mt-4 text-sm text-gray-600">{SIZE_GUIDE_FIT_ADVICE}</p>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default OptionSelect
